import React, { useState } from 'react';
import { CriticalApp } from '../../types';
import { NotesModal } from '../../components/applications/NotesModal';

interface ApplicationsTabProps {
  criticalApps?: CriticalApp[];
}

const ApplicationsTab: React.FC<ApplicationsTabProps> = ({ criticalApps = [] }) => {
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [currentNotes, setCurrentNotes] = useState<string | null>(null);
  const [modalTitle, setModalTitle] = useState('');
  return (
    <div className="px-4 py-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_40px_rgba(15,42,79,0.08)]">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-sky-50 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-[#0F2A4F] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-white">
              Applications
            </span>
            <h1 className="text-2xl font-bold text-[#0F2A4F]">Top 10 Critical Applications</h1>
          </div>
          <p className="mt-1.5 max-w-3xl text-sm text-slate-600">
            Core applications used across the customer environment, with quick visibility into usage, hosting footprint, tiering, and supporting notes.
          </p>
        </div>

        <div className="p-5">
          {criticalApps.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-500">
              No critical applications found.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-full border-collapse bg-white">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[220px]">Application Name</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[180px]">Usage</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[160px]">Location</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[120px]">Tier</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[280px]">Notes</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {criticalApps.map((app, index) => (
                    <tr
                      key={index}
                      className="border-b border-slate-200 transition-colors hover:bg-slate-50/80"
                    >
                      <td className="px-4 py-4 text-slate-900 font-semibold align-top">
                        <div className="max-w-[260px] leading-5">{app.name}</div>
                      </td>
                      <td className="px-4 py-4 text-slate-700 align-top">
                        <div className="max-w-[200px] leading-5">{app.usage}</div>
                      </td>
                      <td className="px-4 py-4 text-slate-700 align-top">
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                          {app.location}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-slate-700 align-top">
                        <span className="inline-flex items-center rounded-full bg-[#0F2A4F]/8 px-3 py-1 text-xs font-semibold text-[#0F2A4F] ring-1 ring-[#0F2A4F]/10">
                          {app.tier}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-slate-700 max-w-md align-top">
                        <div 
                          className="cursor-pointer rounded-lg bg-slate-50 px-3 py-2 text-sm leading-6 transition-colors hover:bg-sky-50 hover:text-sky-700 line-clamp-3"
                          onClick={() => {
                            setCurrentNotes(app.customer_facing_notes);
                            setModalTitle('Notes');
                            setIsNotesModalOpen(true);
                          }}
                        >
                          {app.customer_facing_notes ? app.customer_facing_notes : '--'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      <NotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        notes={currentNotes}
        title={modalTitle}
      />
    </div>
  );
};

export default ApplicationsTab;
